/* Homepage section behaviour.
 *
 * Each section registers an initialiser that only runs once the section scrolls
 * near the viewport, so nothing but the hero costs anything during the initial
 * paint. No GSAP or Framer Motion: every interaction here is a class toggle or a
 * scroll call, and the animation itself is CSS.
 *
 * The design's custom cursor, MouseGlow and Web Audio hover ticks are
 * deliberately absent — each ran a permanent requestAnimationFrame loop, and the
 * cursor's `cursor: none` broke touch entirely.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const q = (sel, root = document) => root.querySelector(sel);
const qa = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ── Lazy section registry ──────────────────────────────────── */

const registry = [];

function register(selector, init) {
  registry.push({ selector, init });
}

function boot() {
  for (const { selector, init } of registry) {
    for (const el of qa(selector)) {
      if (el.dataset.hfReady === '1') continue;
      el.dataset.hfReady = '1';
      observeOnce(el, () => init(el));
    }
  }
}

// Sections initialise 200px before they scroll into view. The hero is already
// on screen at load, so its observer fires immediately.
function observeOnce(el, fn) {
  if (!('IntersectionObserver' in window)) {
    fn();
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      fn();
    },
    { rootMargin: '200px' }
  );
  io.observe(el);
}

/* ── Reveal on scroll ───────────────────────────────────────── */

function initReveal() {
  const targets = qa('[data-hf-reveal]');
  if (targets.length === 0) return;

  if (REDUCED.matches || !('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-revealed'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  targets.forEach((t) => io.observe(t));
}

/* ── Hero carousel ──────────────────────────────────────────── */

register('[data-hf-hero]', (hero) => {
  const slides = qa('[data-hf-slide]', hero);
  const panels = qa('[data-hf-panel]', hero);
  const dots = qa('[data-hf-dot]', hero);
  const pauseBtn = q('[data-hf-hero-pause]', hero);
  if (slides.length < 2) return;

  const duration = Number(hero.dataset.hfInterval) || 5000;
  const autoplay = hero.dataset.hfAutoplay !== 'false' && !REDUCED.matches;

  hero.style.setProperty('--hf-hero-duration', `${duration}ms`);

  let index = 0;
  let timer = null;
  let paused = !autoplay;

  // Slides past the first ship their media inside an inert <template> so the
  // rest of the carousel does not compete with the first frame. Video slides
  // stay preload="none" until the slide on screen can play, then the next
  // clip is allowed to buffer.
  const hydrate = (slide) => {
    if (!slide) return;
    const tpl = q('[data-hf-slide-media]', slide);
    if (!tpl) return;
    tpl.replaceWith(tpl.content.cloneNode(true));
  };

  const videoOf = (slide) => (slide ? q('[data-hf-hero-video]', slide) : null);
  const currentIsVideo = () => slides[index]?.hasAttribute('data-hf-video-slide');

  const narrow = window.matchMedia('(max-width: 749px)');
  const ensureSource = (video) => {
    if (!video || video.getAttribute('src')) return;
    const next = (narrow.matches && video.dataset.srcMobile) || video.dataset.src;
    if (next) video.src = next;
  };

  const warmFollowing = () => {
    const next = slides[(index + 1) % slides.length];
    hydrate(next);
    const video = videoOf(next);
    if (!video) return;
    ensureSource(video);
    video.preload = 'auto';
  };

  let warmToken = 0;
  const armWarmFollowing = () => {
    const token = ++warmToken;
    const run = () => {
      if (token !== warmToken) return;
      warmFollowing();
    };
    const current = videoOf(slides[index]);
    if (current) {
      current.addEventListener('canplaythrough', run, { once: true });
      if (current.readyState >= 4) run();
      return;
    }
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1);
  };

  const syncVideos = () => {
    const nextIndex = (index + 1) % slides.length;
    slides.forEach((slide, i) => {
      const video = videoOf(slide);
      if (!video) return;
      if (i === index) {
        if (!paused) {
          ensureSource(video);
          video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      } else {
        video.pause();
        video.currentTime = 0;
        if (i !== nextIndex) video.preload = 'none';
      }
    });
  };

  const show = (next) => {
    index = (next + slides.length) % slides.length;

    hydrate(slides[index]);

    const videoSlide = currentIsVideo();
    hero.classList.toggle('is-video', videoSlide);
    hero.style.setProperty('--hf-hero-video-progress', '0%');

    slides.forEach((s, i) => {
      const on = i === index;
      s.classList.toggle('is-active', on);
      s.toggleAttribute('aria-hidden', !on);
    });

    panels.forEach((p, i) => {
      const on = i === index;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });

    dots.forEach((d, i) => {
      const on = i === index;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', String(on));
      if (on) {
        // Restart the CSS progress sweep by forcing a reflow on the fill.
        const fill = q('.hf-hero__dot-fill', d);
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }
    });

    armWarmFollowing();
  };

  const schedule = () => {
    clearTimeout(timer);
    syncVideos();
    if (paused || currentIsVideo()) return;
    timer = setTimeout(() => {
      show(index + 1);
      schedule();
    }, duration);
  };

  const setPaused = (value) => {
    paused = value;
    hero.classList.toggle('is-paused', paused);
    if (pauseBtn) {
      pauseBtn.setAttribute('aria-pressed', String(paused));
      q('[data-hf-icon-pause]', pauseBtn)?.toggleAttribute('hidden', paused);
      q('[data-hf-icon-play]', pauseBtn)?.toggleAttribute('hidden', !paused);
    }
    if (paused) clearTimeout(timer);
    schedule();
  };

  hero.addEventListener(
    'timeupdate',
    (event) => {
      const video = event.target;
      if (!video?.matches?.('[data-hf-hero-video]')) return;
      const slide = video.closest('[data-hf-slide]');
      if (!slide || Number(slide.dataset.hfSlide) !== index || !video.duration) return;
      const pct = Math.min((video.currentTime / video.duration) * 100, 100);
      hero.style.setProperty('--hf-hero-video-progress', `${pct}%`);
    },
    true
  );

  hero.addEventListener(
    'ended',
    (event) => {
      const video = event.target;
      if (!video?.matches?.('[data-hf-hero-video]') || paused) return;
      const slide = video.closest('[data-hf-slide]');
      if (!slide || Number(slide.dataset.hfSlide) !== index) return;
      show(index + 1);
      schedule();
    },
    true
  );

  dots.forEach((dot, i) =>
    dot.addEventListener('click', () => {
      show(i);
      schedule();
    })
  );

  pauseBtn?.addEventListener('click', () => setPaused(!paused));

  // Autoplay only while the hero is actually on screen.
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) schedule();
        else clearTimeout(timer);
      },
      { threshold: 0.15 }
    ).observe(hero);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearTimeout(timer);
    else schedule();
  });

  if (currentIsVideo()) hero.classList.add('is-video');
  setPaused(!autoplay);
  armWarmFollowing();

  // Jumping straight to a distant slide should not show an empty frame while
  // its image downloads, so hydrate on intent rather than on activation.
  dots.forEach((dot, i) => {
    const warm = () => hydrate(slides[i]);
    dot.addEventListener('pointerenter', warm, { once: true });
    dot.addEventListener('focus', warm, { once: true });
  });
});

/* ── Featured strip ─────────────────────────────────────────── */

register('[data-hf-featured]', (section) => {
  const track = q('[data-hf-featured-track]', section);
  const prev = q('[data-hf-featured-prev]', section);
  const next = q('[data-hf-featured-next]', section);
  if (!track) return;

  const step = () => {
    const card = q('.hf-featured__card', track);
    if (!card) return track.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
  next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

  const sync = () => {
    const max = track.scrollWidth - track.clientWidth - 1;
    prev?.toggleAttribute('disabled', track.scrollLeft <= 0);
    next?.toggleAttribute('disabled', track.scrollLeft >= max);
  };

  track.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('resize', sync, { passive: true });
  sync();
});

/* ── Category tabs, in-card carousels, popups ───────────────── */

register('[data-hf-cats]', (section) => {
  // Tabs
  const tabs = qa('[data-hf-cat-tab]', section);
  const panels = qa('[data-hf-cat-panel]', section);

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const id = tab.dataset.hfCatTab;
      tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      panels.forEach((p) => {
        p.hidden = p.dataset.hfCatPanel !== id;
      });
    });
  });

  // Two-product cards alternate on a timer, and pause on hover so the visitor
  // can read the one they are looking at.
  qa('[data-hf-cat-carousel]', section).forEach((card) => {
    const slides = qa('[data-hf-cat-slide]', card);
    const dots = qa('[data-hf-cat-dot]', card);
    if (slides.length < 2) return;

    let i = 0;
    let timer = null;
    let hovered = false;

    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, x) => {
        const on = x === i;
        s.classList.toggle('is-active', on);
        s.toggleAttribute('aria-hidden', !on);
      });
      dots.forEach((d, x) => {
        const on = x === i;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', String(on));
      });
    };

    const tick = () => {
      if (!hovered) show(i + 1);
    };

    dots.forEach((d, x) =>
      d.addEventListener('click', () => {
        show(x);
        hovered = true;
      })
    );

    card.addEventListener('mouseenter', () => (hovered = true));
    card.addEventListener('mouseleave', () => (hovered = false));

    if (!REDUCED.matches) timer = setInterval(tick, 4000);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearInterval(timer);
      else if (!REDUCED.matches) timer = setInterval(tick, 4000);
    });
  });

  // Popup modals
  qa('[data-hf-popup-open]', section).forEach((trigger) => {
    const modal = document.getElementById(trigger.dataset.hfPopupOpen);
    if (!modal) return;

    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      q('.hf-cats__modal-close', modal)?.focus();
    };

    const close = () => {
      modal.hidden = true;
      document.body.style.overflow = '';
      lastFocused?.focus();
    };

    trigger.addEventListener('click', open);
    qa('[data-hf-popup-close]', modal).forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  });
});

/* ── Shot-on videos ─────────────────────────────────────────── */

register('[data-hf-shot]', (section) => {
  const track = q('[data-hf-shot-track]', section);
  const prev = q('[data-hf-shot-prev]', section);
  const next = q('[data-hf-shot-next]', section);
  const dots = qa('[data-hf-shot-dots] .hf-shot__dot', section);

  const step = () => {
    if (!track) return 0;
    const item = q('.hf-shot__item', track);
    if (!item) return track.clientWidth * 0.44;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
    return item.getBoundingClientRect().width + gap;
  };

  const count = track ? qa('.hf-shot__item', track).length : 0;

  if (track && count) {
    // The design parks the arrows on the centered card and moves one card
    // per click. A continuous marquee slides the videos under those arrows.
    let index = count > 1 ? 1 : 0;

    const apply = (animate) => {
      const motion = animate && !REDUCED.matches;
      track.style.transition = motion ? 'transform 0.5s ease' : 'none';
      track.style.transform = `translate3d(${-step() * index}px, 0, 0)`;
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    apply(false);

    const go = (delta) => {
      index = (index + delta + count) % count;
      apply(true);
    };

    prev?.addEventListener('click', () => go(-1));
    next?.addEventListener('click', () => go(1));
    window.addEventListener('resize', () => apply(false));

    // Arrows are hidden on the phone. A sideways drag moves one card; a
    // vertical drag still scrolls the page (touch-action: pan-y on the track).
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
    });
    const finishSwipe = (event) => {
      if (pointerId == null || event.pointerId !== pointerId) return;
      pointerId = null;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
      go(dx < 0 ? 1 : -1);
    };
    window.addEventListener('pointerup', finishSwipe);
    window.addEventListener('pointercancel', (event) => {
      if (event.pointerId === pointerId) pointerId = null;
    });
  }

  qa('[data-hf-video]', section).forEach((video) => {
    const wrap = video.closest('.hf-shot__media');
    const toggle = wrap ? q('[data-hf-video-toggle]', wrap) : null;

    // The src is held in data-src so no video bytes are requested until the
    // card is actually near the viewport.
    const load = () => {
      if (video.getAttribute('src')) return;
      const narrow = window.matchMedia('(max-width: 749px)').matches;
      const next = (narrow && video.dataset.srcMobile) || video.dataset.src;
      if (next) video.src = next;
    };

    const setIcons = (playing) => {
      if (!toggle) return;
      q('[data-hf-icon-play]', toggle)?.toggleAttribute('hidden', playing);
      q('[data-hf-icon-pause]', toggle)?.toggleAttribute('hidden', !playing);
      toggle.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    };

    toggle?.addEventListener('click', () => {
      load();
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });

    video.addEventListener('play', () => setIcons(true));
    video.addEventListener('pause', () => setIcons(false));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            load();
            if (!REDUCED.matches) video.play().catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.5 }
      ).observe(video);
    }
  });
});

/* ── Boot ───────────────────────────────────────────────────── */

function init() {
  boot();
  initReveal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
